<template>
  <base-collapse
    v-model="modelValue"
    class="f-col"
    :class="[brandClasses.wrapper, { 'children:px-4': !variant }, { 'pb-3': isExpanded }]"
    :class-content
    :class-toggle
    data-test-id="vf-accordion"
    :value
    @close="emit('close')"
    @expand="emit('expand')"
  >
    <template #toggle="{ expanded }">
      <slot :expanded name="toggle">
        <span class="flex items-center between" :class="brandClasses.toggle[size]" data-test-id="vf-accordion-toggle">
          <div class="flex gap-2">
            <vf-icon
              v-if="titleIcon"
              v-style:c="titleColor"
              :dir="titleIcon.dir"
              :name="titleIcon.icon"
              :size
              :src="titleIcon.src"
            />
            <span v-style:c="titleColor" :class="classTitle ?? brandClasses.title[size]">
              <slot name="title">{{ title }}</slot>
            </span>
          </div>
          <vf-icon
            v-if="iconCollapseName"
            v-style:c="titleColor"
            :name="expanded ? iconCollapseName : iconName"
            :size="iconSize[size]"
          />
          <vf-icon
            v-else
            v-style:c="titleColor"
            :dir="expanded ? 'up' : 'down'"
            :name="iconName"
            :size="iconSize[size]"
          />
        </span>
      </slot>
    </template>
    <template #default>
      <div :class="brandClasses.content" data-test-id="vf-accordion-content">
        <slot />
      </div>
    </template>
  </base-collapse>
</template>

<script lang="ts" setup>
import type { CSSClass, Responsive } from '#types/common'
import type { IconName } from '#types/icon'
import type { Icon } from '#types/target'

export type Props = {
  /**
   * Text that will be rendered in top section if no toggle-slot passed
   */
  title?: string
  /**
   * Icon that will be rendered in top section if no toggle-slot passed
   */
  icon?: IconName
  /**
   * Icon that will be shown when accordion is expanded. If not provided then default chevron would be rotated.
   */
  iconCollapse?: IconName
  /**
   * Icon that will be shown left to the accordion's title
   */
  titleIcon?: Icon
  /**
   * If 'sm' selected then titles are smaller and less vertical space is used.
   */
  size?: 'sm' | 'md'
  /**
   * If 'aligned' selected then border and content
   * align to the title text and expand/collapse icon.
   */
  variant?: 'aligned'
  /**
   * Classes to be applied to content container.
   */
  classContent?: CSSClass
  /**
   * Classes to be applied to toggle button.
   */
  classToggle?: CSSClass
  /**
   * Optional title class to be applied to the accordion title
   */
  classTitle?: CSSClass
  /**
   * Optional title color in responsive format
   */
  titleColor?: Responsive<string | null>
  /**
   * Allows for use within a group where only one accordion can be expanded at a time.
   * In this case, all accordions in the group should be bound to the same model value
   * and each individual accordion should have a unique `value`.
   */
  value?: string
  /**
   * Overrides the expand/collapse toggle icon's size classes (e.g. `h-4 w-4`),
   * independent of `size` (which also affects the title).
   */
  classToggleIcon?: CSSClass
}

const { icon, iconCollapse, size = 'md' } = defineProps<Props>()

const emit = defineEmits<Emits>()

defineSlots<Slots>()

export type Slots = {
  toggle: (props: { expanded: boolean }) => void
  title: () => void
  default: () => void
}

export type Emits = { expand: [], close: [] }

const modelValue = defineModel<boolean | string>('open')

const isExpanded = computed(() => !!modelValue.value)

const { brandClasses, fallbackIcon, fallbackIconCollapse, iconSize } = useAppConfig().components.vf.accordion

const iconName = computed(() => icon || fallbackIcon)
const iconCollapseName = computed(() => iconCollapse ?? fallbackIconCollapse)
</script>
